// Demo mode is scoped to the one tab that signed in for it.
//
// It has to be: the API layer answers from fixtures *before* it looks at
// authentication, so any surface that believes it is in demo mode will show the demo
// identity and silently discard writes. A flag on the shared Privacy Bee domain would
// reach a real signed-in session in another tab and do exactly that. Session storage is
// per-tab, so the blast radius is the tab the demo was started in.
//
// The auth app cannot write this — it is a different origin — so the handoff travels on
// the URL (`?demo=1`) and the routing middleware turns it into this flag.
//
// The value is mirrored into a ref because storage is not reactive: anything reading the
// flag through a computed would cache the boot-time answer and never see it switched on
// mid-navigation.

import { ref } from 'vue';

export const DEMO_KEY = 'pb-demo';

/**
 * What the demo account holds. `?demo=1` runs the fully licensed persona; `?demo=locked` runs the
 * same persona without the paid capabilities, which is how the entitlement-refusal states are
 * walked without an unlicensed account of one's own.
 */
export type DemoPlan = 'licensed' | 'locked';

/** The `?demo=` value that starts each plan, and the token the tab stores for it. */
const PLAN_TOKENS: Record<DemoPlan, string> = { licensed: '1', locked: 'locked' };

/** The plan a `?demo=` value starts, or null for a value that starts no demo at all. */
export function demoPlanFromQuery(value: unknown): DemoPlan | null {
    const token = String(value);

    return (Object.keys(PLAN_TOKENS) as DemoPlan[]).find((candidate) => PLAN_TOKENS[candidate] === token) ?? null;
}

function readPlan(): DemoPlan | null {
    if (import.meta.server) {
        return null;
    }

    try {
        return demoPlanFromQuery(window.sessionStorage.getItem(DEMO_KEY));
    } catch {
        // Storage can throw when cookies are blocked; a demo that cannot start is far
        // better than one that leaks into a real session.
        return null;
    }
}

const plan = ref<DemoPlan | null>(readPlan());

export function isDemoMode(): boolean {
    return plan.value !== null;
}

/** Whether the running demo is the one without the paid capabilities. */
export function isDemoPlanLocked(): boolean {
    return plan.value === 'locked';
}

export function enableDemoMode(started: DemoPlan = 'licensed'): void {
    try {
        window.sessionStorage.setItem(DEMO_KEY, PLAN_TOKENS[started]);
    } catch {
        // Keep the in-memory flag so the current page still runs as a demo.
    }
    plan.value = started;
}

export function disableDemoMode(): void {
    try {
        window.sessionStorage.removeItem(DEMO_KEY);
    } catch {
        // Nothing to clean up if storage was never writable.
    }
    plan.value = null;
}
